/**
 * AddMemberFlow — Iter269 (COACHDIA Pulse, zéro simulation)
 * ==========================================================
 *
 * Flow RÉEL d'ajout d'un nouveau membre à une équipe déjà évaluée.
 *
 *   [Écran 1 — Saisie]  → POST /teams/{id}/waves mode=addition
 *         ↓
 *   [Écran 2 — Suivi]   → polling GET /teams/{id} toutes 8s
 *         ↓  (submission reçue)
 *   [Écran 3 — Rapport] → POST /generate-report sur wave addition
 *         ↓
 *   onSuccess(updatedTeam)
 *
 * Garanties :
 *  ❌ aucune simulation — le nouveau membre passe un VRAI test
 *  ❌ aucun résultat affiché avant complétion réelle
 *  ❌ aucun membre virtuel accepté
 *  ✅ traçabilité : invitation réelle + lien magique + SMTP
 *  ✅ génération rapport v2 = agrégation anciens + nouveau (backend cross-wave)
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  X, UserPlus, Mail, Clock, Check, Loader2, ArrowRight, RotateCw, FileText,
} from 'lucide-react';
import { getApiUrl } from '../../config/environment';

const API = getApiUrl();
const authHeaders = () => {
  const t = localStorage.getItem('token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const STEP = { INPUT: 'input', TRACKING: 'tracking', GENERATING: 'generating', DONE: 'done' };

export default function AddMemberFlow({ open, onClose, teamId, teamName, onReportGenerated }) {
  const [step, setStep] = useState(STEP.INPUT);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [waveId, setWaveId] = useState(null);         // batch_id de la nouvelle wave
  const [memberStatus, setMemberStatus] = useState('not_started');  // not_started|in_progress|completed
  const [pollError, setPollError] = useState(null);

  const reset = () => {
    setStep(STEP.INPUT); setName(''); setEmail(''); setLoading(false);
    setWaveId(null); setMemberStatus('not_started'); setPollError(null);
  };
  const closeAll = () => { reset(); onClose(); };

  /* ---------- Step 1 → 2 : création wave addition ---------- */
  const submitInvite = async () => {
    if (!name.trim()) return toast.error('Nom complet requis');
    if (!email.trim() || !email.includes('@')) return toast.error('Email valide requis');
    setLoading(true);
    try {
      const r = await axios.post(
        `${API}/team-pulse/teams/${teamId}/waves`,
        {
          wave_type: 'addition',
          new_participants: [{ email: email.trim().toLowerCase(), full_name: name.trim() }],
          batch_name: `${teamName} — Ajout de ${name.trim()}`,
          validity_days: 7,
        },
        { headers: authHeaders() },
      );
      setWaveId(r.data.wave?.id);
      setStep(STEP.TRACKING);
      toast.success(`Invitation envoyée à ${name.trim()}`);
    } catch (e) {
      const msg = e.response?.data?.detail || 'Impossible d\'envoyer l\'invitation.';
      toast.error(typeof msg === 'string' ? msg : 'Erreur inconnue.');
    } finally {
      setLoading(false);
    }
  };

  /* ---------- Step 2 : polling suivi complétion ---------- */
  const pollStatus = useCallback(async () => {
    if (!waveId) return;
    try {
      const r = await axios.get(`${API}/team-pulse/teams/${teamId}`, { headers: authHeaders() });
      const waves = r.data.waves || [];
      const currentWave = waves.find((w) => w.batch.id === waveId);
      if (!currentWave) return;
      const invitations = currentWave.batch.invitations || currentWave.invitations || [];
      // Recherche via batch.invited_emails + participants_map (fallback tolerant)
      const invitedEmails = currentWave.batch.invited_emails || [];
      const targetEmail = email.trim().toLowerCase();
      // Fallback : on ne reçoit pas les invitations inline ici → utiliser progress
      const prog = currentWave.progress || {};
      if (prog.completed > 0) {
        setMemberStatus('completed');
      } else if (prog.in_progress > 0) {
        setMemberStatus('in_progress');
      } else {
        setMemberStatus('not_started');
      }
      setPollError(null);
    } catch (e) {
      setPollError(e.response?.data?.detail || 'Erreur de synchronisation');
    }
  }, [waveId, teamId, email]);

  useEffect(() => {
    if (step !== STEP.TRACKING || !waveId) return;
    pollStatus();
    const iv = setInterval(pollStatus, 8000);
    return () => clearInterval(iv);
  }, [step, waveId, pollStatus]);

  /* ---------- Step 2 → 3 : génération rapport v2 ---------- */
  const generateV2 = async () => {
    if (!waveId) return;
    setStep(STEP.GENERATING);
    setLoading(true);
    try {
      const r = await axios.post(
        `${API}/team-pulse/batches/${waveId}/generate-report`,
        {},
        { headers: authHeaders() },
      );
      toast.success('Rapport enrichi généré avec succès');
      setStep(STEP.DONE);
      if (onReportGenerated) onReportGenerated(r.data.batch);
    } catch (e) {
      const msg = e.response?.data?.detail || 'Génération impossible.';
      toast.error(typeof msg === 'string' ? msg : 'Erreur inconnue.');
      setStep(STEP.TRACKING);
    } finally {
      setLoading(false);
    }
  };

  /* ---------- Relance email ---------- */
  const handleRemind = async () => {
    try {
      // Récupère invitation_id depuis batch
      const r = await axios.get(`${API}/team-pulse/batches/${waveId}`, { headers: authHeaders() });
      const inv = (r.data.invitations || []).find(
        (i) => i.email.toLowerCase() === email.trim().toLowerCase(),
      );
      if (!inv) return toast.error('Invitation introuvable');
      await axios.post(
        `${API}/team-pulse/batches/${waveId}/remind`,
        { invitation_id: inv.id },
        { headers: authHeaders() },
      );
      toast.success('Relance envoyée');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Relance impossible');
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" data-testid="pulse-add-member-modal">
      <div className="bg-[#F5F1EA] max-w-2xl w-full max-h-[92vh] overflow-y-auto rounded-2xl shadow-2xl relative">
        {/* Header */}
        <div className="sticky top-0 bg-[#1F1A12] text-white px-8 py-5 flex items-start justify-between rounded-t-2xl z-10">
          <div>
            <div className="text-[10px] tracking-[0.3em] text-[#F3D060] uppercase font-semibold">
              COACHDIA · PULSE · Ajout de membre
            </div>
            <h2 className="font-serif text-2xl mt-1">Ajouter un membre à l'équipe</h2>
            <div className="text-[#EEE0BE] text-sm mt-0.5 italic font-light">
              {teamName} · Test réel obligatoire
            </div>
          </div>
          <button onClick={closeAll} className="text-white/70 hover:text-white transition" data-testid="pulse-add-member-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Stepper */}
        <div className="px-8 pt-6">
          <Stepper current={step} />
        </div>

        <div className="p-8">
          {step === STEP.INPUT && (
            <InputScreen
              name={name} setName={setName}
              email={email} setEmail={setEmail}
              loading={loading} onSubmit={submitInvite}
            />
          )}
          {step === STEP.TRACKING && (
            <TrackingScreen
              memberName={name} memberEmail={email}
              status={memberStatus} pollError={pollError}
              onGenerate={generateV2} onRemind={handleRemind}
            />
          )}
          {step === STEP.GENERATING && <GeneratingScreen />}
          {step === STEP.DONE && (
            <DoneScreen memberName={name} onClose={closeAll} />
          )}
        </div>
      </div>
    </div>
  );
}

/* =============================================================================
 * Sub-screens
 * =============================================================================*/

const Stepper = ({ current }) => {
  const steps = [
    { id: STEP.INPUT, label: 'Invitation' },
    { id: STEP.TRACKING, label: 'Test réel' },
    { id: STEP.GENERATING, label: 'Rapport v2' },
  ];
  const currentIdx = steps.findIndex((s) => s.id === current);
  return (
    <div className="flex items-center gap-2" data-testid="pulse-add-member-stepper">
      {steps.map((s, i) => {
        const isDone = currentIdx > i || current === STEP.DONE;
        const isActive = currentIdx === i;
        return (
          <React.Fragment key={s.id}>
            <div className="flex items-center gap-2">
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-serif font-semibold transition ${
                isDone ? 'bg-[#5B8A6B] text-white'
                : isActive ? 'bg-[#1F1A12] text-[#F3D060]'
                : 'bg-[#E5DFD0] text-[#6B5E48]'
              }`}>
                {isDone ? <Check className="w-3.5 h-3.5" /> : i + 1}
              </div>
              <span className={`text-[11px] uppercase tracking-wider font-semibold ${
                isActive ? 'text-[#1F1A12]' : 'text-[#6B5E48]'
              }`}>{s.label}</span>
            </div>
            {i < steps.length - 1 && (
              <div className={`flex-1 h-[2px] transition ${
                isDone ? 'bg-[#5B8A6B]' : 'bg-[#E5DFD0]'
              }`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

const InputScreen = ({ name, setName, email, setEmail, loading, onSubmit }) => (
  <div className="space-y-5" data-testid="pulse-add-member-input">
    <div className="bg-[#F3D060]/10 border border-[#F3D060]/40 rounded-lg p-4 text-sm text-[#1F1A12] leading-relaxed">
      <span className="font-semibold">Test réel obligatoire</span> — le nouveau membre recevra
      un lien de test par email. Une fois son test complété, vous pourrez générer un
      <span className="italic"> nouveau rapport enrichi</span> sans écraser l'existant.
    </div>

    <div>
      <label className="block text-[11px] uppercase tracking-[0.25em] text-[#92710A] font-semibold mb-2">
        Nom complet du nouveau membre
      </label>
      <input
        value={name} onChange={(e) => setName(e.target.value)}
        placeholder="Ex. Camille Durand"
        className="w-full px-4 py-3 border border-[#6B5E48]/30 rounded-lg bg-white focus:border-[#92710A] focus:outline-none"
        data-testid="pulse-add-member-name"
      />
    </div>
    <div>
      <label className="block text-[11px] uppercase tracking-[0.25em] text-[#92710A] font-semibold mb-2">
        Email (pour l'envoi du lien de test)
      </label>
      <input
        type="email" value={email} onChange={(e) => setEmail(e.target.value)}
        placeholder="camille.durand@entreprise.com"
        className="w-full px-4 py-3 border border-[#6B5E48]/30 rounded-lg bg-white focus:border-[#92710A] focus:outline-none"
        data-testid="pulse-add-member-email"
      />
    </div>

    <button
      onClick={onSubmit} disabled={loading}
      className="w-full bg-[#1F1A12] text-white py-3.5 rounded-full font-medium hover:bg-[#2A2319] transition flex items-center justify-center gap-2 disabled:opacity-50"
      data-testid="pulse-add-member-submit"
    >
      {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Envoi de l'invitation...</> :
        <><Mail className="w-4 h-4" /> Envoyer l'invitation</>}
    </button>
    <div className="text-xs text-[#6B5E48] text-center italic">
      Validité 7 jours · Lien unique magique · Un seul test par personne
    </div>
  </div>
);

const TrackingScreen = ({ memberName, memberEmail, status, pollError, onGenerate, onRemind }) => {
  const badge = {
    not_started: { bg: '#8A7A5E', lbl: 'Invité' },
    in_progress: { bg: '#C8A64B', lbl: 'Test en cours' },
    completed: { bg: '#5B8A6B', lbl: 'Complété' },
  }[status] || { bg: '#8A7A5E', lbl: 'Invité' };

  return (
    <div className="space-y-5" data-testid="pulse-add-member-tracking">
      <div className="bg-white border border-[#E5DFD0] rounded-xl p-6">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-[#92710A] text-white flex items-center justify-center font-serif text-lg">
            {(memberName[0] || '?').toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-medium text-[#1F1A12] truncate">{memberName}</div>
            <div className="text-xs text-[#6B5E48] truncate">{memberEmail}</div>
          </div>
          <div className="shrink-0">
            <span className="text-[10px] uppercase tracking-wider px-3 py-1.5 rounded-full font-semibold text-white"
                  style={{ background: badge.bg }} data-testid="pulse-add-member-status">
              {badge.lbl}
            </span>
          </div>
        </div>
      </div>

      {status !== 'completed' && (
        <div className="bg-[#FAF5EA] border border-[#E5DFD0] rounded-xl p-5 text-sm text-[#6B5E48] leading-relaxed">
          <div className="flex items-start gap-3">
            <Loader2 className="w-4 h-4 animate-spin text-[#92710A] shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="font-medium text-[#1F1A12]">En attente du test de {memberName}...</div>
              <div className="mt-1">
                Synchronisation automatique toutes les 8 secondes. Vous pouvez fermer
                cette fenêtre et revenir plus tard — le suivi sera toujours accessible
                depuis le dashboard.
              </div>
              {pollError && (
                <div className="mt-2 text-[#B94A48] text-xs italic">{pollError}</div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-3">
        <button
          onClick={onRemind}
          className="flex-1 bg-white border border-[#6B5E48]/30 text-[#1F1A12] py-3 rounded-full text-sm font-medium hover:bg-[#FAF5EA] transition flex items-center justify-center gap-2"
          data-testid="pulse-add-member-remind"
          disabled={status === 'completed'}
        >
          <RotateCw className="w-4 h-4" /> Relancer par email
        </button>
        <button
          onClick={onGenerate}
          disabled={status !== 'completed'}
          className="flex-[2] bg-[#1F1A12] text-white py-3 rounded-full text-sm font-medium hover:bg-[#2A2319] transition flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="pulse-add-member-generate-report"
        >
          {status === 'completed' ? (
            <><FileText className="w-4 h-4" /> Générer le rapport v2</>
          ) : (
            <><Clock className="w-4 h-4" /> En attente de complétion</>
          )}
        </button>
      </div>
    </div>
  );
};

const GeneratingScreen = () => (
  <div className="py-16 text-center" data-testid="pulse-add-member-generating">
    <Loader2 className="w-10 h-10 animate-spin text-[#92710A] mx-auto mb-4" />
    <div className="font-serif text-xl text-[#1F1A12]">Génération du rapport enrichi en cours...</div>
    <p className="text-sm text-[#6B5E48] mt-2 max-w-md mx-auto">
      Agrégation des données anciennes et nouvelles, calcul d'impact réel, enrichissement
      éditorial. Ne fermez pas cette fenêtre.
    </p>
  </div>
);

const DoneScreen = ({ memberName, onClose }) => (
  <div className="py-8 text-center" data-testid="pulse-add-member-done">
    <div className="w-14 h-14 rounded-full bg-[#5B8A6B] text-white flex items-center justify-center mx-auto mb-4">
      <Check className="w-7 h-7" />
    </div>
    <h3 className="font-serif text-2xl text-[#1F1A12] mb-2">
      Rapport v2 disponible.
    </h3>
    <p className="text-sm text-[#6B5E48] max-w-md mx-auto mb-6 leading-relaxed">
      L'intégration de <span className="font-semibold text-[#1F1A12]">{memberName}</span> a été
      mesurée sur données réelles. Le rapport précédent reste intact pour comparaison.
    </p>
    <button
      onClick={onClose}
      className="bg-[#1F1A12] text-white px-6 py-3 rounded-full font-medium hover:bg-[#2A2319] transition inline-flex items-center gap-2"
      data-testid="pulse-add-member-open-report"
    >
      Ouvrir le nouveau rapport <ArrowRight className="w-4 h-4" />
    </button>
  </div>
);
